import { Suspense, useState, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import companyLogo from '../assets/company-logo.png';
import AuthBrandedLoader from '../components/AuthBrandedLoader';
import PixelRippleCanvas from '../components/PixelRippleCanvas';
import MascotDuo from '../components/mascot/MascotDuo';
import { MascotContext } from '../contexts/MascotContext';

const AUTH_FEATURES = [
  { n: '01', label: 'Customer Management' },
  { n: '02', label: 'Service Request Tracking' },
  { n: '03', label: 'Proforma & Final Invoicing' },
  { n: '04', label: 'Material Management' },
];

export default function AuthLayout() {
  const { isDark, toggleTheme } = useTheme();
  const [logoLoaded, setLogoLoaded] = useState(false);
  const onLogoLoad = useCallback(() => setLogoLoaded(true), []);
  const leftPanelRef = useRef(null);
  const mascotRef = useRef(null);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex">
      {/* ── Brand Panel ──────────────────────────────────────────────────── */}
      <aside
        ref={leftPanelRef}
        className="relative hidden w-1/2 overflow-hidden bg-primary-500 lg:flex"
      >
        <PixelRippleCanvas containerRef={leftPanelRef} />

        {/* Drifting cross pattern */}
        <div
          className="pointer-events-none absolute inset-[-80px] opacity-[0.14] motion-safe:animate-auth-pattern-drift motion-reduce:animate-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          aria-hidden
        />

        {/* Soft vignette for depth */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/20"
          aria-hidden
        />

        {/* Brand content — vertically centered, matched max-width with form */}
        <div className="relative z-10 w-full flex items-center justify-center px-10 py-16 xl:px-16">
          <div className="w-full max-w-md flex flex-col items-center text-white">
            <div
              className="auth-logo-frame relative mb-10 flex h-36 w-36 items-center justify-center bg-white/95 p-5 shadow-auth-float motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '0ms' }}
            >
              {!logoLoaded && (
                <div className="auth-logo-skeleton absolute inset-4 motion-reduce:animate-none" aria-hidden />
              )}
              <img
                src={companyLogo}
                alt="Civil Tech Veritas company logo"
                width={144}
                height={144}
                decoding="async"
                onLoad={onLogoLoad}
                className={`relative z-10 h-auto max-h-[5.5rem] w-auto max-w-[85%] object-contain transition-opacity duration-500 ease-out motion-reduce:opacity-100 ${
                  logoLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>

            <p
              className="mb-3 font-ledger-mono text-xs font-bold tracking-[0.16em] uppercase text-white/85 motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '90ms' }}
            >
              Enterprise · Resource Planning
            </p>
            <h1
              className="mb-4 text-center font-serif text-5xl font-bold tracking-tight leading-[1.05] motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '140ms' }}
            >
              Civil Tech<br />Veritas
            </h1>
            <div
              className="h-0.5 w-16 bg-white motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '180ms' }}
              aria-hidden
            />
            <p
              className="mt-5 text-center font-serif text-[15px] italic leading-relaxed text-white/90 motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '240ms' }}
            >
              A unified workspace for enquiries, quotations, service requests
              and invoicing — built for modern operations.
            </p>

            {/* Modules index */}
            <div
              className="mt-12 w-full motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '320ms' }}
            >
              <div className="border-b-2 border-white/40 pb-2 mb-2 flex items-center gap-2">
                <span className="font-ledger-mono text-xs tracking-[0.16em] uppercase font-semibold text-white/90">
                  Modules · Index
                </span>
              </div>
              <ul className="divide-y divide-white/15 border-b border-white/15">
                {AUTH_FEATURES.map((feat, i) => (
                  <li
                    key={feat.n}
                    className="group flex items-baseline gap-4 py-2.5 transition duration-300 ease-out will-change-transform motion-safe:hover:translate-x-1"
                    style={{ transitionDelay: `${i * 30}ms` }}
                  >
                    <span
                      className="font-ledger-mono text-xs font-bold tracking-wider text-white tabular-nums w-7"
                      aria-hidden
                    >
                      {feat.n}
                    </span>
                    <span className="font-serif text-sm text-white/85 transition-colors duration-300 group-hover:text-white">
                      {feat.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </aside>

      {/* ── Form Panel ───────────────────────────────────────────────────── */}
      <section className="relative flex w-full flex-col lg:w-1/2 bg-white dark:bg-slate-950">
        {/* Floating theme toggle — no header bar, mirrors brand panel cleanness */}
        <button
          type="button"
          onClick={toggleTheme}
          className="
            absolute top-6 right-6 z-20
            border-2 border-slate-300 dark:border-slate-700 p-2
            text-slate-700 dark:text-slate-300 transition-colors
            hover:border-primary-500 hover:text-primary-500
            dark:hover:border-primary-400 dark:hover:text-primary-400
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500
          "
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Form content — same vertical anchor + matched padding as brand */}
        <div className="relative w-full flex-1 flex items-center justify-center px-6 py-16 sm:px-10 xl:px-16">
          <div className="w-full max-w-md">
            {/* Mobile brand strip — only when brand panel is hidden */}
            <div className="mb-10 flex flex-col items-center gap-3 lg:hidden motion-safe:animate-auth-fade-up motion-reduce:opacity-100">
              <div className="auth-logo-frame relative flex h-20 w-20 items-center justify-center bg-white p-3 shadow-auth-float">
                {!logoLoaded && (
                  <div className="auth-logo-skeleton absolute inset-3 motion-reduce:animate-none" aria-hidden />
                )}
                <img
                  src={companyLogo}
                  alt=""
                  width={80}
                  height={80}
                  decoding="async"
                  onLoad={onLogoLoad}
                  className={`relative z-10 h-10 w-auto object-contain transition-opacity duration-500 ease-out motion-reduce:opacity-100 ${
                    logoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </div>
              <div className="text-center">
                <p className="font-ledger-mono text-xs tracking-[0.16em] uppercase text-slate-600 dark:text-slate-400">
                  Civil Tech Veritas
                </p>
                <h1 className="font-serif text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Enterprise <span className="text-primary-600 dark:text-primary-400">·</span> ERP
                </h1>
              </div>
            </div>

            {/* Mascot duo — sits above the sign-in card on desktop */}
            <div
              className="mb-6 hidden lg:flex justify-center motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '100ms' }}
            >
              <MascotDuo ref={mascotRef} delay={0.45} className="h-28 w-full max-w-xs" />
            </div>

            <div
              className="motion-safe:animate-auth-fade-up motion-reduce:opacity-100"
              style={{ animationDelay: '160ms' }}
            >
              <MascotContext.Provider value={mascotRef}>
                <Suspense fallback={<AuthBrandedLoader />}>
                  <Outlet />
                </Suspense>
              </MascotContext.Provider>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
